/*
 * VQ Cross-Selling "Wird oft zusammen gekauft" (PDP)
 *
 * Auswahl der Zubehoer-Produkte, Live-Summe und gemeinsames Hinzufuegen in
 * den Warenkorb ueber /cart/add.js (items[]). Der Cart-Drawer des Themes wird
 * mit der bestehenden renderContents()-Logik aktualisiert - keine parallele
 * Warenkorb-Architektur.
 *
 * Angezeigt wird nur das Zubehoer. Das Hauptprodukt der PDP hat keine eigene
 * Zeile, wandert beim Klick aber immer mit in den Warenkorb - Variante und
 * Menge kommen live aus dem regulaeren Produktformular. Ohne Haken ist der
 * Klick also ein normaler Einzelkauf.
 *
 * Gerechnet wird ausschliesslich mit den echten Shopify-Preisen der
 * gewaehlten Varianten - keine Rabattlogik.
 */
if (!customElements.get('vq-cross-sell')) {
  customElements.define(
    'vq-cross-sell',
    class VqCrossSell extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('[data-xsell-row]'));
        if (!this.rows.length) return;

        this.moneyFormat = this.dataset.moneyFormat || window.Shopify?.money_format || '{{amount}} €';

        // Hauptprodukt: Preis und Verfuegbarkeit als Startwert aus Liquid,
        // Variante und Menge spaeter live aus dem Produktformular.
        this.mainPrice = parseInt(this.dataset.mainPrice, 10) || 0;
        this.mainAvailable = this.dataset.mainAvailable === 'true';
        // Eigene Spur der gewaehlten Variante, damit die Karte nicht davon
        // abhaengt, dass ein Produktformular auf der Seite existiert.
        this.mainVariant = this.dataset.mainVariantId || '';
        this.mainForm = document.getElementById(this.dataset.productFormId);
        this.quantityInput = document.getElementById(this.dataset.quantityId);

        this.totalEl = this.querySelector('[data-xsell-total]');
        this.ctaEl = this.querySelector('[data-xsell-cta]');
        this.button = this.querySelector('[data-xsell-submit]');
        this.errorEl = this.querySelector('[data-xsell-error]');
        this.expressEl = this.querySelector('[data-xsell-express]');

        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (this.cart && this.button) this.button.setAttribute('aria-haspopup', 'dialog');

        this.addEventListener('change', this.onChange.bind(this));
        // Varianten-Select liegt im <label> der Zeile - Klicks duerfen die
        // Checkbox nicht mitschalten.
        this.querySelectorAll('[data-xsell-variants]').forEach((select) => {
          select.addEventListener('click', (event) => event.stopPropagation());
        });
        this.button?.addEventListener('click', this.onSubmit.bind(this));
        this.quantityInput?.addEventListener('change', () => this.refresh());
        this.quantityInput?.addEventListener('input', () => this.refresh());

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            // Nur auf die eigene Produkt-Section reagieren - Quick-Add-Modals
            // auf derselben Seite senden dasselbe Event.
            const sectionId = event?.data?.sectionId;
            if (sectionId && this.dataset.sectionId && String(sectionId) !== this.dataset.sectionId) return;
            const variant = event?.data?.variant;
            this.mainAvailable = Boolean(variant?.available);
            this.mainPrice = variant?.price ?? 0;
            if (variant?.id) this.mainVariant = String(variant.id);
            // Dawn baut den Mengen-Input beim Variantenwechsel neu auf.
            this.quantityInput = document.getElementById(this.dataset.quantityId);
            this.refresh();
          });
        }

        this.refresh();
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      /* ── Hauptprodukt ───────────────────────────────────────────── */

      mainQuantity() {
        const value = parseInt(this.quantityInput?.value, 10);
        return value > 0 ? value : 1;
      }

      mainVariantId() {
        const input = this.mainForm?.querySelector('[name="id"]');
        if (input && !input.disabled && input.value) return input.value;
        return this.mainVariant;
      }

      /* ── Zeilen-Helfer ──────────────────────────────────────────── */

      isSelected(row) {
        return row.querySelector('[data-xsell-toggle]')?.checked === true;
      }

      amountOf(row) {
        return parseInt(row.querySelector('[data-xsell-amount]')?.value, 10) || 0;
      }

      variantIdOf(row) {
        return row.querySelector('[data-xsell-variant-id]')?.value || '';
      }

      selectedRows() {
        return this.rows.filter((row) => this.isSelected(row) && this.variantIdOf(row));
      }

      /* ── Interaktion ────────────────────────────────────────────── */

      onChange(event) {
        const select = event.target.closest('[data-xsell-variants]');
        if (select) {
          const row = select.closest('[data-xsell-row]');
          const option = select.options[select.selectedIndex];
          if (row && option) {
            row.querySelector('[data-xsell-variant-id]').value = option.value;
            row.querySelector('[data-xsell-amount]').value = option.dataset.amount || '0';
            const priceEl = row.querySelector('[data-xsell-price]');
            if (priceEl) priceEl.textContent = option.dataset.price || '';
            // Variantenwechsel waehlt die Zeile aktiv mit aus.
            const toggle = row.querySelector('[data-xsell-toggle]');
            if (toggle) toggle.checked = true;
          }
        }
        this.hideError();
        this.refresh();
      }

      /* ── Berechnung & Anzeige ───────────────────────────────────── */

      refresh() {
        const selected = this.selectedRows();
        const accessories = selected.reduce((sum, row) => sum + this.amountOf(row), 0);
        const main = this.mainAvailable ? this.mainPrice * this.mainQuantity() : 0;
        const total = main + accessories;

        if (this.totalEl) this.totalEl.textContent = this.formatMoney(total);

        const canSubmit = this.mainAvailable || selected.length > 0;
        if (this.ctaEl) {
          const label = selected.length ? this.dataset.ctaLabel : this.dataset.ctaLabelSingle;
          this.ctaEl.textContent = canSubmit
            ? `${label} · ${this.formatMoney(total)}`
            : this.dataset.unavailableLabel;
        }
        if (this.button) this.button.disabled = !canSubmit;

        // Express-Checkout umgeht den Warenkorb und kann nur das Produkt im
        // eigenen Formular kaufen. Sobald Zubehoer angehakt ist, wuerde er die
        // Auswahl stillschweigend verwerfen - deshalb dann ausblenden.
        if (this.expressEl) {
          this.expressEl.hidden = selected.length > 0 || !this.mainAvailable;
          const variantInput = this.expressEl.querySelector('[data-xsell-express-variant]');
          if (variantInput) variantInput.value = this.mainVariantId();
          const qtyInput = this.expressEl.querySelector('[data-xsell-express-qty]');
          if (qtyInput) qtyInput.value = this.mainQuantity();
        }
      }

      /* ── Warenkorb ──────────────────────────────────────────────── */

      onSubmit() {
        if (this.button.classList.contains('is-loading')) return;

        const items = [];
        if (this.mainAvailable && this.mainVariantId()) {
          items.push({ id: Number(this.mainVariantId()), quantity: this.mainQuantity() });
        }
        this.selectedRows().forEach((row) => {
          items.push({ id: Number(this.variantIdOf(row)), quantity: 1 });
        });
        if (!items.length) return;

        this.hideError();
        this.button.classList.add('is-loading');
        this.button.setAttribute('aria-disabled', 'true');

        const body = { items };

        if (this.cart) {
          body.sections = this.cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          this.cart.setActiveElement?.(this.button);
        }

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showError(response.description || response.message);
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'vq-cross-sell',
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'vq-cross-sell',
                cartData: response,
              });
            }

            // cart-notification erwartet einen einzelnen Artikel-Key; bei
            // mehreren Positionen zeigen wir die zuletzt hinzugefuegte.
            if (!response.key && Array.isArray(response.items) && response.items.length) {
              const last = response.items[response.items.length - 1];
              response.key = last.key;
              response.id = last.id;
            }

            this.cart.classList.remove('is-empty');
            this.cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
            this.showError(this.dataset.errorGeneric);
          })
          .finally(() => {
            this.button.classList.remove('is-loading');
            this.button.removeAttribute('aria-disabled');
          });
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || this.dataset.errorGeneric;
        this.errorEl.hidden = false;
      }

      hideError() {
        if (this.errorEl) this.errorEl.hidden = true;
      }

      /* ── Preisformat (shop.money_format) ────────────────────────── */

      formatMoney(cents) {
        const value = Number(cents) || 0;

        const build = (precision, thousands, decimal) => {
          const parts = (value / 100).toFixed(precision).split('.');
          const int = parts[0].replace(/(\d)(?=(\d{3})+$)/g, `$1${thousands}`);
          return parts[1] ? `${int}${decimal}${parts[1]}` : int;
        };

        return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, name) => {
          switch (name) {
            case 'amount':
              return build(2, ',', '.');
            case 'amount_no_decimals':
              return build(0, ',', '.');
            case 'amount_with_comma_separator':
              return build(2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return build(0, '.', ',');
            case 'amount_with_space_separator':
              return build(2, ' ', ',');
            case 'amount_no_decimals_with_space_separator':
              return build(0, ' ', ',');
            case 'amount_with_apostrophe_separator':
              return build(2, "'", '.');
            default:
              return match;
          }
        });
      }
    }
  );
}
